import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Ban,
  ClipboardList,
  Clock,
  FileCheck2,
  FileX2,
  History,
  Printer,
  SendHorizontal,
  StickyNote,
  Tag,
  Undo2,
} from 'lucide-react';
import {
  approveTask,
  approveTaskForSignature,
  breakTaskSignatures,
  changeVisitType,
  getTaskSignatures,
  getVisit,
  getVisitPdf,
  getVisitTypeChangeOptions,
  markVisitAsMissed,
  returnTaskToClinician,
  setVisitOrderNotRequired,
  signTask,
  submitTaskWithoutSignature,
  unmarkVisitAsMissed,
  updateTaskNotes,
} from '../../services/api';
import { getVisitEvvReadingsVisitsVisitIdEvvGet as getVisitEvv } from '../../services/api/sdk.gen';
import type {
  MissedVisitDataCreate,
  TaskSignatureResponse,
  VisitResponse,
  VisitTypeOption,
} from '../../services/api/types.gen';
import {
  getBusinessErrorMessage,
  getConflictCode,
} from '../../utils/apiErrorDetail';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import { usePdfExport, type PdfSource } from '../../hooks/usePdfExport';
import { useToast } from '../../hooks/useToastContext';
import type { TaskHeaderSecondaryAction } from '../task/TaskHeaderControls';
import {
  resolveAttestationFamily,
  selectAttestationForFamily,
  type ActiveAttestationsMap,
  type SigningAttestation,
} from '../signing/attestations';
import type {
  ESignatureCredentialInput,
  SignatureDateInput,
} from '../signing/esignatureCredentialApi';
import { useSignatureBypassAction } from '../signing/useSignatureBypassAction';
import {
  changeVisitTypeChoices,
  type ChangeVisitTypeChoice,
} from './changeVisitTypeChoices';
import { changeVisitTypeErrorCopy } from './visitTypeChangeCopy';
import { PatientSignatureIcon } from './PatientSignatureIcon';
import {
  type PatientSignature,
  patientSignatureLabel,
} from './patientSignatureLabel';
import { socConflictCopyFromError } from './socConflictCopy';
import { useDeferredDialogOpener } from '../task/useDeferredDialogOpener';
import {
  WAIVE_EVAL_ORDER_FALLBACK_COPY,
  waiveEvalOrderRefusalCopy,
} from './waiveEvalOrderCopy';

export interface OasisWarning {
  proceed: () => void;
  proceedLabel: string;
}

export interface UseVisitDetailActionsArgs {
  visitId: string;
  visit: VisitResponse | null;
  isDeleted: boolean;
  isEditable: boolean;
  isMissedVisit: boolean;
  canApprove: boolean;
  canBypassSignature: boolean;
  canSubmitWithoutSignature: boolean;
  canReturnToClinician: boolean;
  canViewWorkLog: boolean;
  /** The Task Details entry, or none for a user who cannot open it. */
  taskDetailsActions: TaskHeaderSecondaryAction[];
  /** The Outside record entry, or none when it is not offered. */
  outsideRecordActions: TaskHeaderSecondaryAction[];
  canViewEvv: boolean;
  canWaiveEvalOrder: boolean;
  hasStagedAnswers: boolean;
  attestationsLoading: boolean;
  taskNotes: string;
  setTaskNotes: React.Dispatch<React.SetStateAction<string>>;
  setVisit: React.Dispatch<React.SetStateAction<VisitResponse | null>>;
  setSignatures: (signatures: TaskSignatureResponse[]) => void;
  setSaveError: React.Dispatch<React.SetStateAction<string | null>>;
  onVisitUpdated?: (visit: VisitResponse) => void;
  attestations: ActiveAttestationsMap | null | undefined;
  /**
   * The agency requires this signer to clear the All CMS scrubber first and it
   * still shows fatals. Stops the sign or approve dialog before it opens.
   */
  blockSign: boolean;
  blockApprove: boolean;
  /** The All CMS scrubber shows fatals: warn before signing even when not blocked. */
  hasOasisFatals: boolean;
  oasisFatalCount: number | null;
  /** The chart's first OASIS scrub is still loading; bypass waits for it. */
  oasisCheckPending: boolean;
  /**
   * Sends the form's unsaved Task Details answers; resolves false if some did
   * not save. A signed chart refuses them, so Sign waits for this first.
   */
  flushBeforeSign?: () => Promise<boolean>;
}

export interface UseVisitDetailActionsResult {
  visitAttestation: SigningAttestation | null;
  signingLoading: boolean;
  approvingLoading: boolean;
  approvingForSignatureLoading: boolean;
  editingLoading: boolean;
  unsignedMoveLoading: boolean;
  markMissedLoading: boolean;
  unmarkMissedLoading: boolean;
  changeTypeLoading: boolean;
  taskNotesLoading: boolean;
  orderNotRequiredLoading: boolean;
  exportingId: string | null;

  signAttestationOpen: boolean;
  setSignAttestationOpen: (open: boolean) => void;
  approveAttestationOpen: boolean;
  setApproveAttestationOpen: (open: boolean) => void;
  approveForSignatureAttestationOpen: boolean;
  setApproveForSignatureAttestationOpen: (open: boolean) => void;
  showNotesDialog: boolean;
  setShowNotesDialog: (open: boolean) => void;
  showMarkMissedDialog: boolean;
  setShowMarkMissedDialog: (open: boolean) => void;
  confirmWaiveOrderOpen: boolean;
  setConfirmWaiveOrderOpen: (open: boolean) => void;
  showChangeTypeDialog: boolean;
  setShowChangeTypeDialog: (open: boolean) => void;
  showTransitionNotesModal: boolean;
  historyOpen: boolean;
  setHistoryOpen: (open: boolean) => void;
  workLogOpen: boolean;
  setWorkLogOpen: (open: boolean) => void;
  evvOpen: boolean;
  setEvvOpen: (open: boolean) => void;
  patientSignatureState: PatientSignature;
  refreshEvvIndicator: () => void;

  visitTypeOptions: VisitTypeOption[];
  visitTypeChoices: ChangeVisitTypeChoice[];
  selectedVisitType: string;
  setSelectedVisitType: (visitTypeId: string) => void;
  draftNotes: string;
  setDraftNotes: (notes: string) => void;
  transitionNotes: string;
  setTransitionNotes: (notes: string) => void;

  handleSignAndSubmit: () => Promise<void>;
  doSignAndSubmit: (
    credential: ESignatureCredentialInput,
    signatureDate?: SignatureDateInput
  ) => Promise<string | null>;
  handleApprove: () => void;
  doApprove: (credential: ESignatureCredentialInput) => Promise<string | null>;
  handleApproveForSignature: () => void;
  doApproveForSignature: (
    credential: ESignatureCredentialInput
  ) => Promise<string | null>;
  handleBreakSignatures: () => void;
  handleSubmitWithoutSignature: () => Promise<void>;
  handleReturnToClinician: () => Promise<void>;
  handleMarkAsMissed: (payload: MissedVisitDataCreate) => Promise<void>;
  handleUnmarkMissed: () => Promise<void>;
  handleChangeVisitType: () => Promise<void>;
  handleToggleOrderNotRequired: () => Promise<void>;
  handleExportPdf: () => void;
  handleSaveNotes: () => Promise<void>;
  handleConfirmTransition: () => Promise<void>;
  handleCancelTransition: () => void;

  bypassDialog: React.ReactNode;
  /**
   * Open state of the "fix OASIS errors first" dialog: undefined when closed,
   * null when open without a known count (the backend refused).
   */
  oasisBlockedCount: number | null | undefined;
  setOasisBlockedCount: (count: number | null | undefined) => void;
  /** Set when that dialog only warns: how to continue past it. */
  oasisWarning: OasisWarning | null;
  secondaryHeaderActions: TaskHeaderSecondaryAction[];
}

/**
 * Workflow actions for VisitDetailPanel: sign, approve, miss, change-type,
 * notes, print, and the Actions-menu openers. OASIS autosave, save-pill, and
 * signature-invalidation-on-edit stay in the panel composition root.
 */
export function useVisitDetailActions({
  visitId,
  visit,
  isDeleted,
  isEditable,
  isMissedVisit,
  canApprove,
  canBypassSignature,
  canSubmitWithoutSignature,
  canReturnToClinician,
  canViewWorkLog,
  taskDetailsActions,
  outsideRecordActions,
  canViewEvv,
  canWaiveEvalOrder,
  hasStagedAnswers,
  attestationsLoading,
  taskNotes,
  setTaskNotes,
  setVisit,
  setSignatures,
  setSaveError,
  onVisitUpdated,
  attestations,
  blockSign,
  blockApprove,
  hasOasisFatals,
  oasisFatalCount,
  oasisCheckPending,
  flushBeforeSign,
}: UseVisitDetailActionsArgs): UseVisitDetailActionsResult {
  const { error: showError, success: showSuccess } = useToast();
  const { exportPdf, exportingId } = usePdfExport();
  const openDeferred = useDeferredDialogOpener();

  const [signingLoading, setSigningLoading] = useState(false);
  const [oasisBlockedCount, setOasisBlockedCount] = useState<
    number | null | undefined
  >(undefined);
  const [oasisWarning, setOasisWarning] = useState<OasisWarning | null>(null);
  /** Open the OASIS errors dialog: refusing when `blocked`, else warning. */
  const promptOasisErrors = useCallback(
    (blocked: boolean, proceed: () => void, proceedLabel: string) => {
      setOasisWarning(blocked ? null : { proceed, proceedLabel });
      setOasisBlockedCount(oasisFatalCount);
    },
    [oasisFatalCount]
  );
  /** The server refused: always the blocking dialog, count unknown. */
  const showOasisRefusal = useCallback(() => {
    setOasisWarning(null);
    setOasisBlockedCount(null);
  }, []);
  const [approvingLoading, setApprovingLoading] = useState(false);
  const [approvingForSignatureLoading, setApprovingForSignatureLoading] =
    useState(false);
  const [editingLoading, setEditingLoading] = useState(false);
  const [unsignedMoveLoading, setUnsignedMoveLoading] = useState(false);
  const [markMissedLoading, setMarkMissedLoading] = useState(false);
  const [unmarkMissedLoading, setUnmarkMissedLoading] = useState(false);
  const [changeTypeLoading, setChangeTypeLoading] = useState(false);
  const [taskNotesLoading, setTaskNotesLoading] = useState(false);
  const [orderNotRequiredLoading, setOrderNotRequiredLoading] = useState(false);
  const [confirmWaiveOrderOpen, setConfirmWaiveOrderOpen] = useState(false);

  const [signAttestationOpen, setSignAttestationOpen] = useState(false);
  const [approveAttestationOpen, setApproveAttestationOpen] = useState(false);
  const [
    approveForSignatureAttestationOpen,
    setApproveForSignatureAttestationOpen,
  ] = useState(false);
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [showMarkMissedDialog, setShowMarkMissedDialog] = useState(false);
  const [showChangeTypeDialog, setShowChangeTypeDialog] = useState(false);
  const [showTransitionNotesModal, setShowTransitionNotesModal] =
    useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [workLogOpen, setWorkLogOpen] = useState(false);
  const [evvOpen, setEvvOpen] = useState(false);
  const [patientSignatureState, setPatientSignatureState] =
    useState<PatientSignature>('missing');
  const [visitTypeOptions, setVisitTypeOptions] = useState<VisitTypeOption[]>(
    []
  );
  const [selectedVisitType, setSelectedVisitType] = useState('');
  const [draftNotes, setDraftNotes] = useState('');
  const [transitionNotes, setTransitionNotes] = useState('');
  const [pendingTransitionAction, setPendingTransitionAction] = useState<
    (() => Promise<void>) | null
  >(null);

  const visitAttestation = useMemo(() => {
    if (!attestations) return null;
    return selectAttestationForFamily(
      attestations,
      resolveAttestationFamily('visit', null, { isMissedVisit })
    );
  }, [attestations, isMissedVisit]);

  const refreshEvvIndicator = useCallback(() => {
    if (!visitId || !canViewEvv || isDeleted) {
      setPatientSignatureState('missing');
      return;
    }
    getVisitEvv({ path: { visit_id: visitId } })
      .then(({ data }) => {
        setPatientSignatureState(data?.patient_signature ?? 'missing');
      })
      .catch(() => {
        setPatientSignatureState('missing');
      });
  }, [visitId, canViewEvv, isDeleted]);

  useEffect(() => {
    refreshEvvIndicator();
  }, [refreshEvvIndicator]);

  const { actionItems: bypassActionItems, dialog: bypassDialog } =
    useSignatureBypassAction({
      taskId: visitId,
      canBypass: canBypassSignature,
      onError: setSaveError,
      onOasisBlocked: showOasisRefusal,
      onBypassed: async () => {
        const [visitData, signaturesData] = await Promise.all([
          getVisit({ path: { visit_id: visitId } }),
          getTaskSignatures({ path: { task_id: visitId } }),
        ]);
        if (visitData?.data) {
          setVisit(visitData.data);
          onVisitUpdated?.(visitData.data);
        }
        if (signaturesData?.data) {
          setSignatures(signaturesData.data);
        }
      },
    });

  const executeWithTransitionNotes = useCallback(
    (action: () => Promise<void>) => {
      setPendingTransitionAction(() => action);
      setTransitionNotes(taskNotes);
      setShowTransitionNotesModal(true);
    },
    [taskNotes]
  );

  const handleConfirmTransition = useCallback(async () => {
    if (!pendingTransitionAction || !visitId) return;

    if (transitionNotes !== taskNotes) {
      try {
        await updateTaskNotes({
          path: { task_id: visitId },
          body: { notes: transitionNotes || null },
        });
        setTaskNotes(transitionNotes);
      } catch (err) {
        console.error('Error saving notes:', err);
        setSaveError('Failed to save task notes.');
        setShowTransitionNotesModal(false);
        setPendingTransitionAction(null);
        return;
      }
    }

    setShowTransitionNotesModal(false);
    await pendingTransitionAction();
    setPendingTransitionAction(null);
  }, [
    pendingTransitionAction,
    visitId,
    transitionNotes,
    taskNotes,
    setTaskNotes,
    setSaveError,
  ]);

  const handleCancelTransition = useCallback(() => {
    setShowTransitionNotesModal(false);
    setPendingTransitionAction(null);
  }, []);

  const doSignAndSubmit = useCallback(
    async (
      credential: ESignatureCredentialInput,
      signatureDate?: SignatureDateInput
    ): Promise<string | null> => {
      if (!visitId || !visitAttestation) return null;

      try {
        setSigningLoading(true);
        setSaveError(null);

        const signResult = await signTask({
          path: { task_id: visitId },
          body: {
            ...credential,
            ...signatureDate,
            attestation_version: visitAttestation.version,
          },
        });

        if (signResult.error) {
          console.error('Error signing visit:', signResult.error);
          if (getConflictCode(signResult.error) === 'OASIS_CMS_FATAL_ERRORS') {
            setSignAttestationOpen(false);
            showOasisRefusal();
            return null;
          }
          const message =
            getBusinessErrorMessage(signResult) ??
            'Failed to sign and submit. Please try again.';
          setSaveError(message);
          return message;
        }

        const [visitData, signaturesData] = await Promise.all([
          getVisit({ path: { visit_id: visitId } }),
          getTaskSignatures({
            path: { task_id: visitId },
          }),
        ]);

        if (visitData?.data) {
          setVisit(visitData.data);
          onVisitUpdated?.(visitData.data);
        }
        if (signaturesData?.data) {
          setSignatures(signaturesData.data);
        }
        setSignAttestationOpen(false);
        return null;
      } catch (err) {
        console.error('Error signing visit:', err);
        const message = 'Failed to sign and submit. Please try again.';
        setSaveError(message);
        return message;
      } finally {
        setSigningLoading(false);
      }
    },
    [
      visitId,
      visitAttestation,
      setSaveError,
      setVisit,
      onVisitUpdated,
      setSignatures,
    ]
  );

  const TASK_DETAIL_UNSAVED_MESSAGE =
    'Location of care or mileage did not save. Check them and try again.';
  // A second click while the flush runs would flush and open twice.
  const signFlushInFlightRef = useRef(false);
  const handleSignAndSubmit = useCallback(async () => {
    if (flushBeforeSign) {
      if (signFlushInFlightRef.current) return;
      signFlushInFlightRef.current = true;
      try {
        if (!(await flushBeforeSign())) {
          setSaveError(TASK_DETAIL_UNSAVED_MESSAGE);
          return;
        }
      } catch (err) {
        console.error('Saving task detail answers before signing failed:', err);
        setSaveError(TASK_DETAIL_UNSAVED_MESSAGE);
        return;
      } finally {
        signFlushInFlightRef.current = false;
      }
    }
    const openSignature = () => setSignAttestationOpen(true);
    if (blockSign || hasOasisFatals) {
      promptOasisErrors(blockSign, openSignature, 'Sign anyway');
      return;
    }
    openSignature();
  }, [
    flushBeforeSign,
    setSaveError,
    blockSign,
    hasOasisFatals,
    promptOasisErrors,
  ]);

  const doApprove = useCallback(
    async (credential: ESignatureCredentialInput): Promise<string | null> => {
      if (!visitId || !visitAttestation) return null;

      try {
        setApprovingLoading(true);
        setSaveError(null);

        const result = await approveTask({
          path: { task_id: visitId },
          body: {
            ...credential,
            attestation_version: visitAttestation.version,
          },
        });

        if (result.error) {
          console.error('Error completing visit:', result.error);
          if (getConflictCode(result.error) === 'OASIS_CMS_FATAL_ERRORS') {
            setApproveAttestationOpen(false);
            showOasisRefusal();
            return null;
          }
          const message =
            getBusinessErrorMessage(result) ??
            'Failed to complete visit. Please try again.';
          setSaveError(message);
          return message;
        }

        const [visitData, signaturesData] = await Promise.all([
          getVisit({ path: { visit_id: visitId } }),
          getTaskSignatures({
            path: { task_id: visitId },
          }),
        ]);

        if (visitData?.data) {
          setVisit(visitData.data);
          onVisitUpdated?.(visitData.data);
        }
        if (signaturesData?.data) {
          setSignatures(signaturesData.data);
        }
        setApproveAttestationOpen(false);
        return null;
      } catch (err) {
        console.error('Error completing visit:', err);
        const message = 'Failed to complete visit. Please try again.';
        setSaveError(message);
        return message;
      } finally {
        setApprovingLoading(false);
      }
    },
    [
      visitId,
      visitAttestation,
      setSaveError,
      setVisit,
      onVisitUpdated,
      setSignatures,
    ]
  );

  const handleApprove = useCallback(() => {
    // Transition notes are saved in handleConfirmTransition before this
    // callback runs; doApprove only needs the attestation dialog (no notes).
    const openApproval = () =>
      executeWithTransitionNotes(async () => {
        setApproveAttestationOpen(true);
      });
    if (blockApprove || hasOasisFatals) {
      promptOasisErrors(blockApprove, openApproval, 'Sign anyway');
      return;
    }
    openApproval();
  }, [
    blockApprove,
    hasOasisFatals,
    promptOasisErrors,
    executeWithTransitionNotes,
  ]);

  const refreshAfterUnsignedMove = useCallback(async () => {
    if (!visitId) return;
    const [visitData, signaturesData] = await Promise.all([
      getVisit({ path: { visit_id: visitId } }),
      getTaskSignatures({ path: { task_id: visitId } }),
    ]);
    if (visitData?.data) {
      setVisit(visitData.data);
      onVisitUpdated?.(visitData.data);
    }
    if (signaturesData?.data) {
      setSignatures(signaturesData.data);
    }
  }, [visitId, setVisit, onVisitUpdated, setSignatures]);

  // The case manager approves the chart but asks the clinician to
  // sign it again, so it lands in Pending Clinician Signature rather than
  // completing. Same gate and same attestation as Approve & Complete.
  const doApproveForSignature = useCallback(
    async (credential: ESignatureCredentialInput): Promise<string | null> => {
      if (!visitId || !visitAttestation) return null;

      try {
        setApprovingForSignatureLoading(true);
        setSaveError(null);

        const result = await approveTaskForSignature({
          path: { task_id: visitId },
          body: {
            ...credential,
            attestation_version: visitAttestation.version,
          },
        });

        if (result.error) {
          console.error('Error returning visit for signature:', result.error);
          if (getConflictCode(result.error) === 'OASIS_CMS_FATAL_ERRORS') {
            setApproveForSignatureAttestationOpen(false);
            showOasisRefusal();
            return null;
          }
          const message =
            getBusinessErrorMessage(result) ??
            'Failed to return the visit for signature. Please try again.';
          setSaveError(message);
          return message;
        }

        await refreshAfterUnsignedMove();
        setApproveForSignatureAttestationOpen(false);
        return null;
      } catch (err) {
        console.error('Error returning visit for signature:', err);
        const message =
          'Failed to return the visit for signature. Please try again.';
        setSaveError(message);
        return message;
      } finally {
        setApprovingForSignatureLoading(false);
      }
    },
    [
      visitId,
      visitAttestation,
      setSaveError,
      showOasisRefusal,
      refreshAfterUnsignedMove,
    ]
  );

  // Same OASIS pre-check as handleApprove, so a chart with fatal errors warns
  // before the attestation rather than failing after it.
  const handleApproveForSignature = useCallback(() => {
    const openApproval = () =>
      executeWithTransitionNotes(async () => {
        setApproveForSignatureAttestationOpen(true);
      });
    if (blockApprove || hasOasisFatals) {
      promptOasisErrors(blockApprove, openApproval, 'Sign anyway');
      return;
    }
    openApproval();
  }, [
    blockApprove,
    hasOasisFatals,
    promptOasisErrors,
    executeWithTransitionNotes,
  ]);

  const handleSubmitWithoutSignature = useCallback(async () => {
    if (!visitId) return;
    try {
      setUnsignedMoveLoading(true);
      setSaveError(null);
      const result = await submitTaskWithoutSignature({
        path: { task_id: visitId },
      });
      if (result.error) {
        console.error('Error submitting without signature:', result.error);
        setSaveError(
          getBusinessErrorMessage(result) ??
            'Failed to submit. Please try again.'
        );
        return;
      }
      await refreshAfterUnsignedMove();
    } catch (err) {
      console.error('Error submitting without signature:', err);
      setSaveError('Failed to submit. Please try again.');
    } finally {
      setUnsignedMoveLoading(false);
    }
  }, [visitId, setSaveError, refreshAfterUnsignedMove]);

  const handleReturnToClinician = useCallback(async () => {
    if (!visitId) return;
    try {
      setUnsignedMoveLoading(true);
      setSaveError(null);
      const result = await returnTaskToClinician({
        path: { task_id: visitId },
      });
      if (result.error) {
        console.error('Error returning to clinician:', result.error);
        setSaveError(
          getBusinessErrorMessage(result) ??
            'Failed to return the chart. Please try again.'
        );
        return;
      }
      await refreshAfterUnsignedMove();
    } catch (err) {
      console.error('Error returning to clinician:', err);
      setSaveError('Failed to return the chart. Please try again.');
    } finally {
      setUnsignedMoveLoading(false);
    }
  }, [visitId, setSaveError, refreshAfterUnsignedMove]);

  const handleBreakSignatures = useCallback(async () => {
    if (!visitId) return;

    const doBreak = async () => {
      try {
        setEditingLoading(true);
        setSaveError(null);

        const result = await breakTaskSignatures({
          path: { task_id: visitId },
        });

        if (result.error) {
          console.error('Error breaking signatures:', result.error);
          setSaveError('Failed to break signatures. Please try again.');
          return;
        }

        const [visitData, signaturesData] = await Promise.all([
          getVisit({ path: { visit_id: visitId } }),
          getTaskSignatures({
            path: { task_id: visitId },
          }),
        ]);

        if (visitData?.data) {
          setVisit(visitData.data);
          onVisitUpdated?.(visitData.data);
        }
        if (signaturesData?.data) {
          setSignatures(signaturesData.data);
        }
      } catch (err) {
        console.error('Error breaking signatures:', err);
        setSaveError('Failed to break signatures. Please try again.');
      } finally {
        setEditingLoading(false);
      }
    };

    executeWithTransitionNotes(doBreak);
  }, [
    visitId,
    setSaveError,
    setVisit,
    onVisitUpdated,
    setSignatures,
    executeWithTransitionNotes,
  ]);

  const handleMarkAsMissed = useCallback(
    async (payload: MissedVisitDataCreate) => {
      if (!visitId) return;
      try {
        setMarkMissedLoading(true);
        setSaveError(null);
        const result = await markVisitAsMissed({
          path: { visit_id: visitId },
          body: payload,
        });
        if (result.error) {
          console.error('Error marking visit as missed:', result.error);
          // A 409 here usually means the billing cycle is locked; the server
          // says so, and a generic "try again" would send the user in circles.
          showError(
            getBusinessErrorMessage(result) ??
              'Unable to mark visit as missed. Please try again.'
          );
          return;
        }
        if (result.data) {
          setVisit(result.data);
          setSignatures([]);
          onVisitUpdated?.(result.data);
        }
        setShowMarkMissedDialog(false);
      } catch (err) {
        console.error('Error marking visit as missed:', err);
        showError(
          getBusinessErrorMessage(err) ??
            'Unable to mark visit as missed. Please try again.'
        );
      } finally {
        setMarkMissedLoading(false);
      }
    },
    [visitId, setSaveError, setVisit, setSignatures, onVisitUpdated, showError]
  );

  const handleUnmarkMissed = useCallback(async () => {
    if (!visitId) return;
    try {
      setUnmarkMissedLoading(true);
      setSaveError(null);
      const result = await unmarkVisitAsMissed({
        path: { visit_id: visitId },
      });
      if (result.error) {
        console.error('Error unmarking visit:', result.error);
        // This 409 is not retryable: another live SOC already holds the slot.
        showError(
          socConflictCopyFromError(result) ??
            getBusinessErrorMessage(result) ??
            'Unable to unmark visit. Please try again.'
        );
        return;
      }
      if (result.data) {
        setVisit(result.data);
        setSignatures([]);
        onVisitUpdated?.(result.data);
      }
    } catch (err) {
      console.error('Error unmarking visit:', err);
      showError(
        socConflictCopyFromError(err) ??
          getBusinessErrorMessage(err) ??
          'Unable to unmark visit. Please try again.'
      );
    } finally {
      setUnmarkMissedLoading(false);
    }
  }, [
    visitId,
    setSaveError,
    setVisit,
    setSignatures,
    onVisitUpdated,
    showError,
  ]);

  // The backend decides which aliases share this visit's form schema; an empty
  // list is what hides the action, so the two can never disagree about what is
  // switchable. Skipped for locked charts, which cannot be renamed anyway.
  useEffect(() => {
    if (!visitId || !isEditable) {
      setVisitTypeOptions([]);
      return;
    }
    let cancelled = false;
    const loadOptions = async () => {
      try {
        const result = await getVisitTypeChangeOptions({
          path: { visit_id: visitId },
        });
        if (!cancelled && result.data) setVisitTypeOptions(result.data);
      } catch (err) {
        // Non-fatal: the action stays hidden rather than blocking the chart.
        console.error('Error loading visit type options:', err);
      }
    };
    void loadOptions();
    return () => {
      cancelled = true;
    };
  }, [visitId, isEditable, visit?.visit_type, visit?.alias_id]);

  const visitTypeChoices = useMemo(
    () => changeVisitTypeChoices(visitTypeOptions, !!visit?.alias_id),
    [visitTypeOptions, visit?.alias_id]
  );

  const handleChangeVisitType = useCallback(async () => {
    const choice = visitTypeChoices.find(c => c.value === selectedVisitType);
    if (!visitId || !choice) return;
    try {
      setChangeTypeLoading(true);
      const result = await changeVisitType({
        path: { visit_id: visitId },
        body: { visit_type: choice.visitType, alias_id: choice.aliasId },
      });
      if (result.error) {
        console.error('Error changing visit type:', result.error);
        // The 409 body is the only error shape carrying a ConflictCode; a 422
        // has no `code` and falls through to the generic retry copy.
        const code = 'code' in result.error ? result.error.code : null;
        showError(changeVisitTypeErrorCopy(code));
        return;
      }
      if (result.data) {
        setVisit(result.data);
        onVisitUpdated?.(result.data);
      }
      setShowChangeTypeDialog(false);
    } catch (err) {
      console.error('Error changing visit type:', err);
      showError(changeVisitTypeErrorCopy(null));
    } finally {
      setChangeTypeLoading(false);
    }
  }, [
    visitId,
    visitTypeChoices,
    selectedVisitType,
    setVisit,
    onVisitUpdated,
    showError,
  ]);

  const orderNotRequired = visit?.order_not_required ?? false;
  const handleToggleOrderNotRequired = useCallback(async () => {
    if (!visitId) {
      // Above the try, so close the confirmation here too rather than leaving
      // it open with nothing in flight behind it.
      setConfirmWaiveOrderOpen(false);
      return;
    }
    const next = !orderNotRequired;
    try {
      setOrderNotRequiredLoading(true);
      const result = await setVisitOrderNotRequired({
        path: { visit_id: visitId },
        body: { order_not_required: next },
      });
      if (result.error || !result.data) {
        console.error('Error changing the order requirement:', result.error);
        // Only the guard refusing a mark gets the removal copy: a 409 on the
        // unmark direction comes from the restore path and would otherwise
        // tell someone who asked for the order back that it cannot be removed.
        // Everything else keeps the ordinary business message, so a 403 still
        // says the user lacks the permission.
        const conflictCode =
          next && getHttpStatus(result) === 409
            ? getConflictCode(result.error)
            : undefined;
        showError(
          conflictCode
            ? waiveEvalOrderRefusalCopy(conflictCode)
            : (getBusinessErrorMessage(result) ??
                WAIVE_EVAL_ORDER_FALLBACK_COPY)
        );
        return;
      }
      setVisit(result.data);
      onVisitUpdated?.(result.data);
      showSuccess(
        next ? 'Marked as not needing an order' : 'Marked as needing an order'
      );
    } catch (err) {
      console.error('Error changing the order requirement:', err);
      // A thrown error carries no response, so there is no code to key off.
      showError(getBusinessErrorMessage(err) ?? WAIVE_EVAL_ORDER_FALLBACK_COPY);
    } finally {
      setOrderNotRequiredLoading(false);
      // Closed here rather than on the success path so a refusal does not leave
      // the confirmation sitting on top of the toast explaining it.
      setConfirmWaiveOrderOpen(false);
    }
  }, [
    visitId,
    orderNotRequired,
    setVisit,
    onVisitUpdated,
    showError,
    showSuccess,
  ]);

  const handleSaveNotes = useCallback(async () => {
    if (!visitId) return;
    if (draftNotes === taskNotes) {
      setShowNotesDialog(false);
      return;
    }
    try {
      setTaskNotesLoading(true);
      setSaveError(null);
      const res = await updateTaskNotes({
        path: { task_id: visitId },
        body: { notes: draftNotes || null },
      });
      setTaskNotes(draftNotes);
      // Reflect the backend body-edit auto-advance (Not Started ->
      // Saved) without refetching the whole chart.
      const nextStatus = res.data?.status;
      if (nextStatus) {
        setVisit(prev => (prev ? { ...prev, status: nextStatus } : prev));
      }
      setShowNotesDialog(false);
    } catch (err) {
      console.error('Error saving task notes:', err);
      setSaveError('Failed to save task notes.');
    } finally {
      setTaskNotesLoading(false);
    }
  }, [visitId, draftNotes, taskNotes, setTaskNotes, setSaveError, setVisit]);

  const handleExportPdf = useCallback(() => {
    if (!visitId) return;
    void exportPdf(
      visitId,
      async (): Promise<PdfSource | null> => {
        const response = await getVisitPdf({
          path: { visit_id: visitId },
          ...(isDeleted && { query: { include_deleted: true } }),
        });
        return response.error || !response.data
          ? null
          : {
              blob: new Blob([response.data as BlobPart], {
                type: 'application/pdf',
              }),
            };
      },
      'visit PDF'
    );
  }, [visitId, isDeleted, exportPdf]);

  const secondaryHeaderActions: TaskHeaderSecondaryAction[] = useMemo(
    () => [
      ...(isEditable && !isMissedVisit
        ? [
            {
              label: 'Mark as Missed',
              icon: <Ban className='h-4 w-4 mr-2' aria-hidden='true' />,
              onSelect: () => {
                openDeferred('markMissed', () => setShowMarkMissedDialog(true));
              },
            },
          ]
        : []),
      ...(isEditable && isMissedVisit
        ? [
            {
              label: unmarkMissedLoading ? 'Unmarking…' : 'Unmark as Missed',
              icon: <Undo2 className='h-4 w-4 mr-2' aria-hidden='true' />,
              onSelect: () => void handleUnmarkMissed(),
              disabled: unmarkMissedLoading,
            },
          ]
        : []),
      // A bypass completes the chart, so it is held to the QA rule too.
      ...bypassActionItems.map(item =>
        oasisCheckPending
          ? { ...item, disabled: true }
          : blockApprove || hasOasisFatals
            ? {
                ...item,
                onSelect: () =>
                  openDeferred('oasisBypass', () =>
                    promptOasisErrors(
                      blockApprove,
                      item.onSelect,
                      'Bypass anyway'
                    )
                  ),
              }
            : item
      ),
      ...outsideRecordActions,
      ...(canSubmitWithoutSignature
        ? [
            {
              label: unsignedMoveLoading
                ? 'Submitting…'
                : 'Submit without signing',
              icon: (
                <SendHorizontal className='h-4 w-4 mr-2' aria-hidden='true' />
              ),
              onSelect: () => void handleSubmitWithoutSignature(),
              // Same reason as the Sign buttons: submitting moves the visit on
              // from its saved answers, which exclude anything still staged.
              disabled: unsignedMoveLoading || hasStagedAnswers,
            },
          ]
        : []),
      ...(canApprove
        ? [
            {
              label: approvingForSignatureLoading
                ? 'Approving…'
                : 'Approve & Return for Signature',
              icon: <Clock className='h-4 w-4 mr-2' aria-hidden='true' />,
              onSelect: () =>
                openDeferred('approveForSignature', handleApproveForSignature),
              // Same disabled set as Approve & Complete: the approval signs
              // the saved answers, which exclude anything still staged.
              disabled:
                approvingForSignatureLoading ||
                oasisCheckPending ||
                attestationsLoading ||
                !visitAttestation ||
                hasStagedAnswers,
            },
          ]
        : []),
      ...(canReturnToClinician
        ? [
            {
              label: unsignedMoveLoading ? 'Returning…' : 'Return to clinician',
              icon: <Undo2 className='h-4 w-4 mr-2' aria-hidden='true' />,
              onSelect: () => void handleReturnToClinician(),
              disabled: unsignedMoveLoading,
            },
          ]
        : []),
      // Only an evaluation whose completion raises a physician signature order,
      // which the backend reports so the menu and the route cannot disagree.
      // Offered on a completed chart too: that is when the order exists.
      ...(canWaiveEvalOrder &&
      !isDeleted &&
      !isMissedVisit &&
      visit?.raises_signature_request
        ? [
            {
              label: orderNotRequiredLoading
                ? 'Saving…'
                : orderNotRequired
                  ? 'Mark as needing an order'
                  : 'Mark as not an order',
              icon: orderNotRequired ? (
                <FileCheck2 className='h-4 w-4 mr-2' aria-hidden='true' />
              ) : (
                <FileX2 className='h-4 w-4 mr-2' aria-hidden='true' />
              ),
              // Only the destructive direction asks first. Deferred because
              // opening a dialog straight from the closing dropdown leaves the
              // page unclickable.
              onSelect: () =>
                orderNotRequired
                  ? void handleToggleOrderNotRequired()
                  : openDeferred('waiveOrder', () =>
                      setConfirmWaiveOrderOpen(true)
                    ),
              disabled: orderNotRequiredLoading,
            },
          ]
        : []),
      ...(isEditable && !isMissedVisit && visitTypeChoices.length > 0
        ? [
            {
              label: 'Change task type',
              icon: <Tag className='h-4 w-4 mr-2' aria-hidden='true' />,
              onSelect: () => {
                setSelectedVisitType('');
                openDeferred('changeType', () => setShowChangeTypeDialog(true));
              },
            },
          ]
        : []),
      // Offered on a deleted chart too, where the dialog is read-only. Editing is
      // withheld there like the rest of the editing, but this dialog is also the
      // only way into the episode's shared log — notes that belong to the whole
      // episode, not to the chart that was deleted — so withholding the action
      // would put them out of reach from a deleted visit entirely.
      {
        label: isDeleted ? 'View notes' : 'Notes',
        icon: <StickyNote className='h-4 w-4 mr-2' aria-hidden='true' />,
        onSelect: () => {
          setDraftNotes(taskNotes);
          setSaveError(null);
          openDeferred('notes', () => setShowNotesDialog(true));
        },
      },
      {
        label: exportingId === visitId ? 'Opening print view...' : 'Print view',
        icon:
          exportingId === visitId ? (
            <div
              className='animate-spin rounded-full h-3 w-3 mr-2 border-b-2 border-current'
              aria-hidden='true'
            />
          ) : (
            <Printer className='h-4 w-4 mr-2' aria-hidden='true' />
          ),
        onSelect: handleExportPdf,
        disabled: exportingId === visitId,
      },
      {
        label: 'View Signature History',
        icon: <History className='h-4 w-4 mr-2' aria-hidden='true' />,
        onSelect: () => {
          openDeferred('history', () => setHistoryOpen(true));
        },
      },
      ...(canViewWorkLog
        ? [
            {
              label: 'Work log',
              icon: (
                <ClipboardList className='h-4 w-4 mr-2' aria-hidden='true' />
              ),
              onSelect: () => {
                openDeferred('workLog', () => setWorkLogOpen(true));
              },
            },
          ]
        : []),
      ...taskDetailsActions,
      // Two independent reasons to withhold this one. EVV access is a permission,
      // and separately the readings route was not widened for deleted visits, so
      // offering it on a deleted chart would only produce an error.
      ...(canViewEvv && !isDeleted
        ? [
            {
              // read-only proof-of-visit captured in the nursing app. Always
              // offered to users with EVV access — the dialog itself shows an empty
              // state when nothing was captured, which is most visits today.
              label: 'Patient Signature',
              icon: (
                <span className='relative mr-2 inline-flex shrink-0'>
                  <span className='sr-only'>
                    {patientSignatureLabel(patientSignatureState)}
                  </span>
                  <PatientSignatureIcon state={patientSignatureState} />
                </span>
              ),
              onSelect: () => {
                openDeferred('evv', () => setEvvOpen(true));
              },
            },
          ]
        : []),
    ],
    [
      isEditable,
      isMissedVisit,
      openDeferred,
      unmarkMissedLoading,
      handleUnmarkMissed,
      bypassActionItems,
      outsideRecordActions,
      blockApprove,
      hasOasisFatals,
      promptOasisErrors,
      oasisCheckPending,
      canSubmitWithoutSignature,
      unsignedMoveLoading,
      handleSubmitWithoutSignature,
      hasStagedAnswers,
      canApprove,
      approvingForSignatureLoading,
      attestationsLoading,
      visitAttestation,
      handleApproveForSignature,
      canReturnToClinician,
      handleReturnToClinician,
      canWaiveEvalOrder,
      visit?.raises_signature_request,
      orderNotRequired,
      orderNotRequiredLoading,
      handleToggleOrderNotRequired,
      visitTypeChoices.length,
      isDeleted,
      taskNotes,
      setSaveError,
      exportingId,
      visitId,
      handleExportPdf,
      canViewWorkLog,
      taskDetailsActions,
      canViewEvv,
      patientSignatureState,
    ]
  );

  return {
    visitAttestation,
    signingLoading,
    approvingLoading,
    approvingForSignatureLoading,
    editingLoading,
    unsignedMoveLoading,
    markMissedLoading,
    unmarkMissedLoading,
    changeTypeLoading,
    taskNotesLoading,
    orderNotRequiredLoading,
    confirmWaiveOrderOpen,
    setConfirmWaiveOrderOpen,
    exportingId,
    signAttestationOpen,
    setSignAttestationOpen,
    approveAttestationOpen,
    setApproveAttestationOpen,
    approveForSignatureAttestationOpen,
    setApproveForSignatureAttestationOpen,
    showNotesDialog,
    setShowNotesDialog,
    showMarkMissedDialog,
    setShowMarkMissedDialog,
    showChangeTypeDialog,
    setShowChangeTypeDialog,
    showTransitionNotesModal,
    historyOpen,
    setHistoryOpen,
    workLogOpen,
    setWorkLogOpen,
    evvOpen,
    setEvvOpen,
    patientSignatureState,
    refreshEvvIndicator,
    visitTypeOptions,
    visitTypeChoices,
    selectedVisitType,
    setSelectedVisitType,
    draftNotes,
    setDraftNotes,
    transitionNotes,
    setTransitionNotes,
    handleSignAndSubmit,
    doSignAndSubmit,
    handleApprove,
    doApprove,
    handleApproveForSignature,
    doApproveForSignature,
    handleBreakSignatures,
    handleSubmitWithoutSignature,
    handleReturnToClinician,
    handleMarkAsMissed,
    handleUnmarkMissed,
    handleChangeVisitType,
    handleToggleOrderNotRequired,
    handleExportPdf,
    handleSaveNotes,
    handleConfirmTransition,
    handleCancelTransition,
    bypassDialog,
    oasisBlockedCount,
    setOasisBlockedCount,
    oasisWarning,
    secondaryHeaderActions,
  };
}
